import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  Clock,
  Briefcase,
  Search,
  Filter,
  Users,
  Coffee,
  Heart,
  Zap,
  Award,
  Globe,
  Building2,
} from "lucide-react";
import {
  EVENTS,
  COMPANY_INFO,
  INDUSTRY_ICONS,
  INDUSTRIES,
  CASE_STUDIES,
} from "../data/mock";
import { TEAM_IMAGES } from "@/data/teamImages";
import {
  EngagementModelsSection,
  FAQSection,
  TechnologiesSection,
  WhatWeDoSection,
} from "@/components/CommonSections";
import api from "../lib/api";
import { toast } from "sonner";

// Render CKEditor-authored job descriptions with the editor's content styles.
import "ckeditor5/ckeditor5-content.css";

// Strip HTML tags to get a plain-text preview for the listing card.
const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const CareersPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const cvInputRef = useRef(null);

  // Load active job posts from the API.
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const { data } = await api.get("/job-posts/active");
        const list = Array.isArray(data)
          ? data
          : data?.data || data?.jobs || [];
        // Map API fields to the shape the UI renders.
        const normalized = list.map((j) => ({
          id: j.id,
          title: j.job_title,
          department: j.role || "",
          location: j.location || "",
          type: j.engagement || "",
          descriptionHtml: j.job_description || "",
          description: stripHtml(j.job_description),
        }));
        setJobs(normalized);
      } catch (error) {
        // Listing shows its empty state if this fails.
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobs();
  }, []);
  const [applyJob, setApplyJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    cv: null,
  });

  const closeApplyModal = () => {
    setApplyJob(null);
    setApplicationForm({
      fullName: "",
      email: "",
      phone: "",
      cv: null,
    });
    setApplicationErrors({});
  };

  const [applicationErrors, setApplicationErrors] = useState({});
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);

  // Validate a single apply-form field; returns an error string ("" when valid).
  const validateApplicationField = (name, value) => {
    const v = (value ?? "").toString().trim();
    switch (name) {
      case "fullName":
        if (!v) return "Full name is required.";
        if (v.length < 2) return "Name must be at least 2 characters.";
        if (v.length > 35) return "Name must be under 35 characters.";
        if (!/^[a-zA-Z\s.'-]+$/.test(v))
          return "Name can only contain letters.";
        return "";
      case "email":
        if (!v) return "Email is required.";
        if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v))
          return "Enter a valid email address.";
        return "";
      case "phone": {
        if (!v) return "Phone number is required.";
        // Allow digits with an optional leading "+".
        if (!/^\+?\d*$/.test(v))
          return "Only numbers and a leading + are allowed.";
        if (v.length > 14) return "Phone must be at most 14 characters.";
        const digits = v.replace(/\D/g, "");
        if (digits.length < 7) return "Enter a valid phone number.";
        return "";
      }
      default:
        return "";
    }
  };

  const validateApplication = () => {
    const fields = ["fullName", "email", "phone"];
    const next = {};
    fields.forEach((f) => {
      const error = validateApplicationField(f, applicationForm[f]);
      if (error) next[f] = error;
    });
    if (!applicationForm.cv) next.cv = "Please upload your CV.";
    setApplicationErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleApplicationChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setApplicationForm((prev) => ({ ...prev, [name]: files[0] }));
      setApplicationErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    let next = value;
    if (name === "fullName") {
      // Allow only letters, spaces and . ' - characters.
      next = value.replace(/[^a-zA-Z\s.'-]/g, "");
    } else if (name === "email") {
      // Allow only characters valid in an email address.
      next = value.replace(/[^A-Za-z0-9._%+@-]/g, "");
    } else if (name === "phone") {
      // Allow only digits and a single leading "+", cap at 14 characters.
      let cleaned = value.replace(/[^\d+]/g, "");
      const hasPlus = cleaned.startsWith("+");
      cleaned = (hasPlus ? "+" : "") + cleaned.replace(/\+/g, "");
      next = cleaned.slice(0, 14);
      setApplicationForm((prev) => ({ ...prev, [name]: next }));
      // Clear any error while typing; validate on blur.
      setApplicationErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    setApplicationForm((prev) => ({ ...prev, [name]: next }));
    // Clear any existing error while typing; validation runs on blur.
    setApplicationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validate a field when it loses focus.
  const handleApplicationBlur = (e) => {
    const { name, value } = e.target;
    setApplicationErrors((prev) => ({
      ...prev,
      [name]: validateApplicationField(name, value),
    }));
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (!validateApplication()) {
      toast.error(
        "We identified an issue with the information you provided. Please review the form again."
      );
      return;
    }

    setApplicationSubmitting(true);

    // The CV is a File, so send everything as multipart/form-data.
    const data = new FormData();
    data.append("job_post_id", applyJob?.id ?? "");
    data.append("full_name", applicationForm.fullName);
    data.append("email", applicationForm.email);
    data.append("phone_number", applicationForm.phone);
    if (applicationForm.cv) data.append("cv", applicationForm.cv);

    try {
      const response = await api.post("/job-responses/add", data);
      toast.success(
        response.data?.message ||
          "Application submitted! We'll be in touch soon.",
      );
      closeApplyModal();
    } catch (error) {
      const result = error.response?.data;
      if (result?.errors) {
        // Surface the first backend validation message.
        const firstError = Object.values(result.errors)[0];
        toast.error(firstError?.[0] || "Failed to submit application");
      } else {
        toast.error(result?.message || "Failed to submit application");
      }
    } finally {
      setApplicationSubmitting(false);
    }
  };
  const departments = [
    "All",
    "Engineering",
    "Design",
    "Product",
    "Sales",
    "Marketing",
  ];

  const benefits = [
    {
      icon: Heart,
      title: "Health & Wellness",
      description:
        "Health insurance coverage for you and your family, including medical emergency support.",
    },
    {
      icon: Coffee,
      title: "Work-Life Balance",
      description:
        "Flexible hours and hybrid/remote options — we trust you to get the work done.",
    },
    {
      icon: Zap,
      title: "Learning & Growth",
      description:
        "Dedicated L&D budget, internal tech talks, certifications, and access to cutting-edge AI and cloud projects.",
    },
    {
      icon: Award,
      title: "Competitive Compensation",
      description:
        "Competitive salaries benchmarked to your expertise, with performance reviews and growth-linked increments.",
    },
    {
      icon: Users,
      title: "Team Events",
      description:
        "Team celebrations, quarterly outings, and a culture that actually makes Mondays feel okay. ",
    },
    {
      icon: Globe,
      title: "Global Opportunities",
      description:
        "Real exposure to international clients across the US, Europe, and APAC — not just domestic work.",
    },
  ];

  const filteredJobs = jobs.filter((job) => {
    const title = (job.title || "").toLowerCase();
    const department = (job.department || "").toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchesSearch = title.includes(term) || department.includes(term);
    const matchesDepartment =
      selectedDepartment === "All" || job.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  // New Stats Data
  const HOME_STATS = [
    { value: 100, suffix: "+", label: "Engineers & Specialists" },
    { value: 200, suffix: "+", label: "Products Shipped" },
    { value: 25, suffix: "+", label: "AI-Powered Products" },
    { value: 180, suffix: "+", label: "Mobile Apps Live" },
  ];

  const AnimatedCounter = ({ value, suffix = "" }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    // Parse numeric value and extract suffix from string values like "99.9%"
    const parseValue = (val) => {
      if (typeof val === "number")
        return { numericValue: val, extractedSuffix: "" };
      const str = String(val);
      const match = str.match(/^([\d.]+)(.*)$/);
      if (match) {
        return {
          numericValue: parseFloat(match[1]),
          extractedSuffix: match[2],
        };
      }
      return { numericValue: 0, extractedSuffix: str };
    };

    const { numericValue, extractedSuffix } = parseValue(value);
    const finalSuffix = suffix || extractedSuffix;
    const hasDecimal = numericValue % 1 !== 0;

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        },
        { threshold: 0.3 },
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => observer.disconnect();
    }, [isVisible]);

    useEffect(() => {
      if (!isVisible) return;

      const duration = 2000;
      const steps = 60;
      const increment = numericValue / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= numericValue) {
          setCount(numericValue);
          clearInterval(timer);
        } else {
          setCount(
            hasDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current),
          );
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }, [isVisible, numericValue, hasDecimal]);

    return (
      <span ref={ref}>
        {hasDecimal ? count.toFixed(1) : count}
        {finalSuffix}
      </span>
    );
  };
  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b32126] font-semibold">Join Our Team</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Build What's Next at Infotech.Works
            </h1>
            <p className="text-xl text-gray-300">
              We're an AI-native engineering company working on real problems —
              IoT, GenAI platforms, mobile apps, and enterprise systems. If you
              want to grow fast and work on meaningful products, this is your
              team.
            </p>
          </div>
        </div>
      </section>

      {/* <section className="py-12 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {HOME_STATS.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-red-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Job Search */}
      {/* <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedDepartment === dept
                      ? "bg-[#b32126] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {dept}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Job Listings */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            Open Positions ({filteredJobs.length})
          </h2>

          {loadingJobs ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-600">Loading open positions…</p>
            </div>
          ) : filteredJobs.length > 0 ? (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  onClick={() => setSelectedJob(job)}
                  className="bg-white px-6 py-7 rounded-xl border border-gray-100 hover:shadow-lg transition-all group cursor-pointer"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#b32126] transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 mt-3">
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <Briefcase className="h-4 w-4" /> {job.department}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <MapPin className="h-4 w-4" /> {job.location}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="h-4 w-4" /> {job.type}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setApplyJob(job);
                      }}
                      className="inline-flex items-center px-6 py-3 bg-[#b32126] text-white font-semibold rounded-lg hover:bg-[#8f1a1f] transition-colors whitespace-nowrap"
                    >
                      Apply Now
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-600">
                No jobs match your search criteria.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Work With Us?
            </h2>
            <p className="text-xl text-gray-600">
              Here's what working at Infotech.Works actually looks like
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-[#b32126]" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Life at Infotech.Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Life @ Infotech.Works
            </h2>
            <p className="text-xl text-gray-600">
              A glimpse into our culture and workplace
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <img
              src={TEAM_IMAGES.img8}
              alt="Team collaboration"
              className="rounded-xl h-48 w-full object-cover"
            />
            <img
              src={TEAM_IMAGES.img13}
              alt="Office space"
              className="rounded-xl h-48 w-full object-cover"
            />
            <img
              src={TEAM_IMAGES.img9}
              alt="Team meeting"
              className="rounded-xl h-48 w-full object-cover"
            />
            <img
              src={TEAM_IMAGES.img4}
              alt="Working together"
              className="rounded-xl h-48 w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Events */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            Upcoming Events
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {EVENTS.map((event) => (
              <div
                key={event.id}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-[#b32126] font-medium mb-2">
                  {event.date}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {event.title}
                </h3>
                <p className="text-gray-600 text-sm">{event.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}
      <WhatWeDoSection />

      {/* Industries Section - Show first 2 rows (12 items on desktop) */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Deep expertise across {INDUSTRIES.length}+ industries, delivering
              specialized solutions
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {INDUSTRIES.slice(0, 12).map((industry) => {
              const IconComponent = INDUSTRY_ICONS[industry.slug] || Building2;
              return (
                <Link
                  key={industry.slug}
                  to={`/industries/${industry.slug}`}
                  className="group p-4 bg-gray-800 rounded-xl text-center hover:bg-[#b32126] transition-all"
                >
                  <div className="w-10 h-10 mx-auto mb-3 bg-gray-700 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <IconComponent className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-gray-300 group-hover:text-white text-sm font-medium block">
                    {industry.title}
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/industries"
              className="inline-flex items-center text-[#b32126] hover:text-[#d42a30] font-semibold"
            >
              View All Industries
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real clients across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CASE_STUDIES.slice(0, 3).map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-medium rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                      {study.timeline}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />
      <FAQSection />

      {/* CTA */}
      <section className="py-20 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Not finding the right fit right now?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            We're growing fast and always on the lookout for strong engineers,
            designers, and product thinkers. Drop us your resume and tell us
            what you're looking for — we'll reach out when the right opportunity
            opens up.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Send Your Resume
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {selectedJob && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-start overflow-y-auto">
          <div className="relative bg-white w-full max-w-4xl my-10 rounded-lg shadow-2xl">
            <button
              onClick={() => setSelectedJob(null)}
              className="absolute top-6 right-6 text-gray-500 hover:text-black text-2xl"
            >
              ✕
            </button>

            {/* Header */}
            <div className="flex items-center gap-4 border-b px-6 ">
              <img
                src={TEAM_IMAGES.logo}
                alt={selectedJob.title}
                className="w-20 h-20 rounded-full bg-white p-2"
              />
              <h2 className="text-md font-bold text-[#b32126] uppercase">
                {selectedJob.title}
              </h2>
            </div>

            {/* Body */}
            <div className="px-10 py-6 space-y-8 max-h-[75vh] overflow-y-auto overflow-x-hidden">
              {/* Meta — department / location / type */}
              <div className="flex flex-wrap gap-4">
                {selectedJob.department && (
                  <span className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Briefcase className="h-4 w-4 text-[#b32126]" />
                    {selectedJob.department}
                  </span>
                )}
                {selectedJob.location && (
                  <span className="flex items-center gap-1.5 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 text-[#b32126]" />
                    {selectedJob.location}
                  </span>
                )}
                {selectedJob.type && (
                  <span className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Clock className="h-4 w-4 text-[#b32126]" />
                    {selectedJob.type}
                  </span>
                )}
              </div>

              {/* Description (HTML from the API) */}
              {selectedJob.descriptionHtml && (
                <div
                  className="ck-content text-gray-700 leading-relaxed [overflow-wrap:anywhere]
                    [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mt-6 [&_h1]:mb-3
                    [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-6 [&_h2]:mb-3
                    [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-4 [&_h3]:mb-2
                    [&_h4]:text-base [&_h4]:font-semibold [&_h4]:text-gray-900 [&_h4]:mt-4 [&_h4]:mb-2
                    [&_p]:my-3
                    [&_ul]:pl-6 [&_ol]:pl-6
                    [&_a]:text-[#b32126] [&_a]:font-medium [&_a]:underline
                    [&_strong]:text-gray-900
                    [&_img]:max-w-full [&_img]:h-auto
                    [&_.table]:overflow-x-auto"
                  dangerouslySetInnerHTML={{
                    __html: selectedJob.descriptionHtml,
                  }}
                />
              )}

              {/* Description (legacy mock fallback) */}
              {selectedJob.fullDescription && (
                <p className="text-gray-700 leading-relaxed">
                  {selectedJob.fullDescription}
                </p>
              )}

              {/* Job Details */}

              {/* Responsibilities */}
              {selectedJob.responsibilities && (
                <div>
                  <h3 className="text-lg font-semibold text-[#b32126] mb-4 uppercase">
                    {selectedJob.responsibilitiesHeading}
                  </h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    {selectedJob.responsibilities.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Skills */}
              {selectedJob.skills && (
                <div>
                  <h3 className="text-lg font-semibold text-[#b32126] mb-4 uppercase tracking-wide">
                    {selectedJob.skillsHeading}
                  </h3>

                  <ul className="list-disc pl-5 space-y-2 text-gray-800 marker:text-gray-800">
                    {selectedJob.skills.map((block, index) => (
                      <React.Fragment key={index}>
                        {block.heading && (
                          <li className="font-normal text-gray-900">
                            {block.heading}
                          </li>
                        )}

                        {block.items.map((item, i) => (
                          <li
                            key={i}
                            className={`leading-relaxed ${
                              block.heading ? "ml-5" : ""
                            }`}
                          >
                            {item}
                          </li>
                        ))}
                      </React.Fragment>
                    ))}
                  </ul>
                </div>
              )}
              {/* Desirable */}
              {selectedJob.desirable && (
                <div>
                  <h3 className="text-lg font-semibold text-[#b32126] mb-4 uppercase">
                    {selectedJob.desirableHeading}
                  </h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    {selectedJob.desirable.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Perks */}
              {selectedJob.perks && (
                <div>
                  <h3 className="text-lg font-semibold text-[#b32126] mb-4 uppercase">
                    {selectedJob.perksHeading}
                  </h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    {selectedJob.perks.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJob.notForYou && (
                <div>
                  <h3 className="text-lg font-semibold text-[#b32126] mb-4 uppercase">
                    {selectedJob.notForYouHeading}
                  </h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    {selectedJob.notForYou.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJob.jobDetails && (
                <div>
                  <h3 className="text-lg font-semibold text-[#b32126] mb-4 uppercase">
                    Job Details:
                  </h3>

                  <div className="space-y-2 text-gray-700">
                    {selectedJob.jobDetails.map((detail, index) => (
                      <div key={index}>
                        <span className="font-semibold">{detail.label}:</span>{" "}
                        {detail.value}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center gap-4 border-t pt-6 mt-6">
                <button
                  onClick={() => {
                    setApplyJob(selectedJob);
                    setSelectedJob(null);
                  }}
                  className="inline-flex items-center px-8 py-3 bg-[#b32126] text-white font-semibold rounded-lg hover:bg-[#8f1a1f] transition-colors"
                >
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {applyJob && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-start overflow-y-auto p-4">
          <div className="relative bg-white w-full max-w-2xl my-10 rounded-lg shadow-2xl">
            <button
              onClick={closeApplyModal}
              className="absolute top-5 right-5 text-gray-500 hover:text-black text-2xl"
            >
              ✕
            </button>

            {/* Header */}
            <div className="border-b px-8 py-6">
              <h2 className="text-xl font-bold text-gray-900">
                Apply for this Position
              </h2>
              <p className="text-[#b32126] font-semibold mt-1">
                {applyJob.title}
              </p>
            </div>

            {/* Form */}
            <form
              onSubmit={handleApplicationSubmit}
              noValidate
              className="px-8 py-6 space-y-5 max-h-[70vh] overflow-y-auto"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-[#b32126]">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  required
                  maxLength={35}
                  value={applicationForm.fullName}
                  onChange={handleApplicationChange}
                  onBlur={handleApplicationBlur}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent ${
                    applicationErrors.fullName
                      ? "border-red-400"
                      : "border-gray-200"
                  }`}
                  placeholder="Enter your full name"
                />
                {applicationErrors.fullName && (
                  <p className="mt-1 text-sm text-red-500">
                    {applicationErrors.fullName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-[#b32126]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  maxLength={100}
                  value={applicationForm.email}
                  onChange={handleApplicationChange}
                  onBlur={handleApplicationBlur}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent ${
                    applicationErrors.email
                      ? "border-red-400"
                      : "border-gray-200"
                  }`}
                  placeholder="Enter your email address"
                />
                {applicationErrors.email && (
                  <p className="mt-1 text-sm text-red-500">
                    {applicationErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-[#b32126]">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  inputMode="numeric"
                  maxLength={14}
                  value={applicationForm.phone}
                  onChange={handleApplicationChange}
                  onBlur={handleApplicationBlur}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#b32126] focus:border-transparent ${
                    applicationErrors.phone
                      ? "border-red-400"
                      : "border-gray-200"
                  }`}
                  placeholder="Enter your phone number"
                />
                {applicationErrors.phone && (
                  <p className="mt-1 text-sm text-red-500">
                    {applicationErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload CV <span className="text-[#b32126]">*</span>
                </label>
                <input
                  ref={cvInputRef}
                  type="file"
                  name="cv"
                  accept=".pdf,.doc,.docx"
                  onChange={handleApplicationChange}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => cvInputRef.current?.click()}
                    className={`shrink-0 py-2 px-4 rounded-lg text-sm font-semibold bg-red-50 text-[#b32126] hover:bg-red-100 transition-colors ${
                      applicationErrors.cv ? "ring-1 ring-red-400" : ""
                    }`}
                  >
                    Choose file
                  </button>
                  <span className="text-sm text-gray-500 truncate">
                    {applicationForm.cv ? applicationForm.cv.name : "No file chosen"}
                  </span>
                </div>
                {applicationErrors.cv && (
                  <p className="mt-1 text-sm text-red-500">
                    {applicationErrors.cv}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={applicationSubmitting}
                className="w-full flex items-center justify-center px-6 py-3 bg-[#b32126] text-white font-semibold rounded-lg hover:bg-[#8f1a1f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {applicationSubmitting ? (
                  "Submitting…"
                ) : (
                  <>
                    Submit Application
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersPage;
